/* eslint-disable camelcase */
import dayjs from 'dayjs';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Table, ConfigProvider, Tag, message } from 'antd';
import { useNavigate } from 'react-router-dom';

// eslint-disable-next-line import/no-cycle
import { GetColumns } from './columns';
import { CustomPagination } from './CustomPagination';
import Loading from './Loading';
import TableHeaders from './PRHeaders';
import Summary from './Summary';
import { DuplicateRemover, getError, RemoveFieldFromObj } from '../../clientUtils';
import AppLayout from '../../Components/Common/Layout';
import { useWindowSize, useUrlState } from '../../Hooks';
import { getFilters, getList, getStatus } from '../../Utils/listing';

const defaultPagination = {
  current: 1,
  pageSize: 15
};

const optionCreator = (value, label) => ({
  value,
  label
});

const transformFilters = (data) => {
  const output = [];
  const inputOptions = [];
  const dateOptions = [];
  const functionMap = {
    input: (element) => {
      inputOptions.push(optionCreator(element.field, element.label));
    },
    date_range: (element) => {
      dateOptions.push(optionCreator(element.field, element.label));
    },
    default: (element) => {
      output.push(element);
    }
  };
  data?.forEach((element) => {
    if (functionMap[element.type]) {
      functionMap[element.type](element);
    } else {
      functionMap.default(element);
    }
  });
  if (inputOptions && inputOptions?.length && inputOptions.length > 0) {
    output.push({
      type: 'filterSearch',
      options: inputOptions
    });
  }
  if (dateOptions && dateOptions?.length && dateOptions.length > 0) {
    output.push({
      type: 'filterDate',
      options: dateOptions
    });
  }
  return output;
};

const getSummary = async (loading, type, selectedFilters, state, pagination, afterFetch, signal) => {
  try {
    loading.start();
    await getStatus(type, selectedFilters, state, pagination, afterFetch, signal);
    loading.finish();
  } catch (error) {
    loading.finish();
    afterFetch([]);
    message.error('Error while fetching Summary data');
  }
};

const getFiltersByType = async (loading, type, afterFetch, signal) => {
  try {
    loading.start();
    await getFilters(type, afterFetch, signal);
    loading.finish();
  } catch (error) {
    loading.finish();
    afterFetch([]);
    message.error('Error while fetching filters');
  }
};

const transformFiltersLabels = (data) => {
  const labels = {};
  data?.forEach((element) => {
    labels[element.field] = element.label;
  });
  return labels;
};

// eslint-disable-next-line default-param-last
const getListing = async (loading, type, selectedFilters = {}, state, pagination, afterFetch, signal) => {
  try {
    loading.start();
    const p = await getList(type, selectedFilters, state, pagination, afterFetch, signal);
    loading.finish(p);
  } catch (error) {
    if (error?.code === 'ERR_CANCELED') return;
    loading.finish();
    afterFetch([]);
    message.error(getError(error));
  }
};

const menuSelection = {
  por: {
    key: 'purchaseRequest',
    group: ['purchases']
  },
  po: { key: 'purchaseOrder', group: ['purchases'] },
  po_asn: { key: 'asn', group: ['purchases'] },
  po_grn: { key: 'po-grn', group: ['purchases'] },
  s_cpo: { key: 'customerPO', group: ['sales', 'sales-domestic'] },
  s_dso: { key: 'domesticSalesOrder', group: ['sales', 'sales-domestic'] },
  s_iso: { key: 'internationalSalesOrder', group: ['sales', 'sales-international'] },
  inventory: { key: 'inventory', group: ['sales'] },
  s_so: { key: 'salesRequest', group: ['sales'] },
  so_grn: { key: 'so-grn', group: ['sales'] },
  so_dgrn: { key: 'so-dgrn', group: ['sales'] },
  so_igrn: { key: 'so-igrn', group: ['sales'] },
  users: { key: 'usersList', group: ['users'] },
  transfer_pricing: { key: 'transfer_pricing', group: ['masters'] },
  cogs: { key: 'cogs', group: ['masters'] },
  ro: { key: 'returnOrder', group: ['sales', 'sales-domestic'] },
  poReturnOrder: { key: 'poReturnOrder', group: ['purchases'] },
  user_access_review: { key: 'userAccessReview', group: ['access-control'] }
};

const Listing = ({ type }) => {
  // loading states
  // when filters or summary are loading rendering skeleton
  // these states are saperate to avoid race condition
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [tabsDisabled, setTabsDisabled] = useState(false);

  // components states
  const [statusFilters, setStatusFilters] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState([]);
  const [filterLabels, setFilterLabels] = useState({});

  // states for fixing layout
  const [tableHeight, setTableHeight] = useState(200);
  const headerRef = useRef(null);
  const layoutRef = useRef(null);
  const size = useWindowSize();

  // navigation for click
  const navigate = useNavigate();

  const [pagination, setPagination] = useUrlState('p', defaultPagination, 'pagination');
  const [selectedFilters, setSelectedFilters] = useUrlState('f', {}, 'filters');
  const previousType = useRef(type);
  const abortControllerRef = useRef({});

  const initializeAbortControllers = (keys) => {
    keys.forEach(abortRequest);
    abortControllerRef.current = Object.fromEntries(keys.map((key) => [key, new AbortController()]));
  };

  const abortRequest = (key) => {
    if (abortControllerRef.current[key]) {
      abortControllerRef.current[key].abort();
    }
  };

  useEffect(() => {
    const apiKeys = ['summary', 'filters', 'listing'];
    initializeAbortControllers(apiKeys);

    let currentFilters = selectedFilters;
    if (previousType.current !== type) {
      setSelectedFilters({});
      currentFilters = {};
      previousType.current = type;
    }

    setTableData([]);
    getSummary(
      {
        start: () => {
          setLoadingSummary(true);
        },
        finish: () => {
          setLoadingSummary(false);
        }
      },
      type,
      RemoveFieldFromObj(currentFilters, 'state'),
      undefined,
      pagination,
      (data) => {
        setStatusFilters(data);
      },
      abortControllerRef.current.summary.signal
    );

    getFiltersByType(
      {
        start: () => {
          setLoadingFilters(true);
        },
        finish: () => {
          setLoadingFilters(false);
        }
      },
      type,
      (data) => {
        setFilters(transformFilters(data));
        setFilterLabels(transformFiltersLabels(data));
      },
      abortControllerRef.current.filters.signal
    );

    getListing(
      {
        start: () => {
          setLoadingList(true);
        },
        finish: (p) => {
          setLoadingList(false);
          setPagination(p);
        }
      },
      type,
      currentFilters,
      undefined,
      pagination,
      (data) => setTableData(data),
      abortControllerRef.current.listing.signal
    );

    return () => {
      apiKeys.forEach(abortRequest);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (type !== 'transfer_pricing' && type !== 'cogs') return;

    const interval = setInterval(() => {
      const hasApprovalInProgress = tableData.some((item) => item.status === 'APPROVAL_IN_PROGRESS');

      if (hasApprovalInProgress) {
        getListing(
          {
            start: () => setLoadingList(true),
            finish: (p) => {
              setLoadingList(false);
              setPagination(p);
            }
          },
          type,
          selectedFilters,
          undefined,
          pagination,
          (data) => setTableData(data),
          abortControllerRef.current.listing.signal
        );
      }
    }, 5000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, [type, tableData, selectedFilters, pagination]);

  useLayoutEffect(() => {
    const headerHeight = headerRef?.current?.getBoundingClientRect();
    const pageHeight = layoutRef?.current?.getBoundingClientRect();
    if (headerHeight && pageHeight) {
      setTableHeight(pageHeight.height - headerHeight.height - 136);
    }
  }, [size]);

  const handlePageChange = async (page, pageSize) => {
    const apiKeys = ['listing'];
    initializeAbortControllers(apiKeys);
    try {
      setPagination({
        ...pagination,
        current: page,
        pageSize
      });
      getListing(
        {
          start: () => {
            setLoadingList(true);
          },
          finish: (p) => {
            setLoadingList(false);
            setPagination(p);
          }
        },
        type,
        selectedFilters,
        undefined,
        {
          ...pagination,
          current: page,
          pageSize
        },
        (data) => setTableData(data),
        abortControllerRef.current.listing.signal
      );
    } catch (error) {
      message.error(getError(error));
    }
  };

  const handleRowClick = (record) => {
    if (type === 'transfer_pricing' || type === 'cogs') return;
    const { id } = record;
    const { state, status } = record;
    const typeMap = {
      po: `/purchases/purchase-order/${id}`,
      por: `/purchases/purchase-request/${id}`,
      po_asn: `/purchases/asn/${id}`,
      po_grn: `/purchases/grn/${id}`,
      s_cpo: `/sales/customer-po/${id}`,
      so_dgrn: `/sales/domestic-grn/${id}`,
      so_igrn: `/sales/international-grn/${id}`,
      s_dso:
        state !== 'DELIVERED' ? `/sales/domestic-sales-order/${id}` : `/sales/domestic-sales-order/${id}/delivered`,
      s_iso:
        state !== 'DELIVERED'
          ? `/sales/international-sales-order/${id}`
          : `/sales/international-sales-order/${id}/delivered`,
      ro: status !== 'DELIVERED' ? `/sales/return/${id}` : `/sales/return/${id}/delivered`,
      users: `/users/${id}`,
      poReturnOrder:
        status !== 'DELIVERED'
          ? `/purchases/return-order/details/${id}`
          : `/purchases/return-order/details/${id}/delivered`,
      user_access_review: `/access-control/user-access-reviews/${id}`
    };
    navigate(typeMap[type]);
  };

  const handleStatusSelect = async (key) => {
    /**
     * make tabs disabled
     * reset pagination to current as 0
     * call getlist with the key as state
     * set key to state for pagination change
     */
    const apiKeys = ['listing'];
    initializeAbortControllers(apiKeys);
    setTabsDisabled(true);
    // setStatus([key]);
    const temp = {
      ...selectedFilters,
      state: [key]
    };
    if (key === 'ALL') delete temp.state;
    setSelectedFilters(temp);
    getListing(
      {
        start: () => {
          setLoadingList(true);
        },
        finish: (p) => {
          setLoadingList(false);
          setPagination(p);
        }
      },
      type,
      temp,
      key,
      {
        ...pagination,
        current: 1
      },
      (data) => setTableData(data),
      abortControllerRef.current.listing.signal
    );
    setTabsDisabled(false);
  };

  /**
   *
   * @param {string} type - type of filter options, input, dateRange
   * @param {string} key - field Id of the filter
   * @param {[string] || string} value - can be an array or a string
   */
  const handleFilterApply = (filterType, key, value) => {
    const apiKeys = ['summary', 'listing'];
    initializeAbortControllers(apiKeys);
    // setting filters values
    let currentFilters = {};
    const filtersTypeMap = {
      options: () => {
        let filterValue = selectedFilters[key]?.filter((f) => f === value);
        if (!filterValue) filterValue = [];
        currentFilters = {
          ...selectedFilters,
          [key]: [...filterValue, ...(value || [])]
        };
        setSelectedFilters(currentFilters);
      },
      input: () => {
        const valuesArray = value.split(',').map((e) => e.trim());
        let filterValue = selectedFilters[key];
        if (!filterValue) filterValue = [];
        const finalFilterValue = DuplicateRemover([...filterValue, ...valuesArray]);
        currentFilters = {
          ...selectedFilters,
          [key]: finalFilterValue
        };
        setSelectedFilters(currentFilters);
      },
      date_range: () => {
        currentFilters = {
          ...selectedFilters,
          [key]: {
            start_date: value[0],
            end_date: value[1]
          }
        };
        setSelectedFilters(currentFilters);
      }
    };

    filtersTypeMap?.[filterType](key, value);
    // fetching list and status for new filters
    // set loading
    getListing(
      {
        start: () => {
          setLoadingList(true);
        },
        finish: (p) => {
          setLoadingList(false);
          setPagination(p);
        }
      },
      type,
      currentFilters,
      undefined,
      defaultPagination,
      (data) => setTableData(data),
      abortControllerRef.current.listing.signal
    );
    getSummary(
      {
        start: () => {
          setLoadingSummary(true);
        },
        finish: () => {
          setLoadingSummary(false);
        }
      },
      type,
      RemoveFieldFromObj(currentFilters, 'state'),
      undefined,
      defaultPagination,
      (data) => setStatusFilters(data),
      abortControllerRef.current.summary.signal
    );
  };

  const handleTagClose = (e, value, field) => {
    // go to items : if array filter if not delete the key
    const apiKeys = ['summary', 'listing'];
    initializeAbortControllers(apiKeys);
    e.preventDefault();
    let currentFilters = {};
    if (Array.isArray(selectedFilters?.[field])) {
      const filterField = selectedFilters?.[field].filter((f) => f !== value);
      currentFilters = {
        ...selectedFilters,
        [field]: filterField
      };
      if (currentFilters[field].length === 0) delete currentFilters[field];
    } else {
      currentFilters = { ...selectedFilters };
      delete currentFilters[field];
    }
    setSelectedFilters(currentFilters);
    getSummary(
      {
        start: () => {
          setLoadingSummary(true);
        },
        finish: () => {
          setLoadingSummary(false);
        }
      },
      type,
      RemoveFieldFromObj(currentFilters, 'state'),
      undefined,
      defaultPagination,
      (data) => {
        setStatusFilters(data);
      },
      abortControllerRef.current.summary.signal
    );
    getListing(
      {
        start: () => {
          setLoadingList(true);
        },
        finish: (p) => {
          setLoadingList(false);
          setPagination(p);
        }
      },
      type,
      currentFilters,
      undefined,
      pagination,
      (data) => setTableData(data),
      abortControllerRef.current.listing.signal
    );
  };

  const handleClearAll = () => {
    const apiKeys = ['summary', 'listing'];
    initializeAbortControllers(apiKeys);
    setSelectedFilters({});
    // setStatus("ALL");
    getSummary(
      {
        start: () => {
          setLoadingSummary(true);
        },
        finish: () => {
          setLoadingSummary(false);
        }
      },
      type,
      {},
      undefined,
      defaultPagination,
      (data) => {
        setStatusFilters(data);
      },
      abortControllerRef.current.summary.signal
    );
    getListing(
      {
        start: () => {
          setLoadingList(true);
        },
        finish: (p) => {
          setLoadingList(false);
          setPagination(p);
        }
      },
      type,
      {},
      undefined,
      pagination,
      (data) => setTableData(data),
      abortControllerRef.current.listing.signal
    );
  };

  return (
    <AppLayout
      className="listing_page"
      openGroup={menuSelection[type].group}
      overrideContent="listing_page_view"
      selected={menuSelection[type].key}>
      {loadingFilters ? <Loading /> : null}
      {!loadingFilters ? (
        <div ref={layoutRef} style={{ height: '100vh' }}>
          <div ref={headerRef} className="header_fixed listing_content">
            <TableHeaders type={type} />
            {/* {loadingSummary && <Skeleton />} */}
            <Summary
              filters={{ filters }}
              handleClearAll={handleClearAll}
              onFilterApply={handleFilterApply}
              onSelect={handleStatusSelect}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              status={selectedFilters?.state?.[0] || 'ALL'}
              statusFilters={statusFilters}
              tabsDisabled={loadingSummary || tabsDisabled}
            />
          </div>
          <div className="listing_filter_values_table">
            <div className="table_selected_filter">
              {Object.keys(selectedFilters)?.map((item) => {
                if (item === 'state') return <></>;
                if (!Array.isArray(selectedFilters[item])) {
                  return (
                    <Tag key={item} closable onClose={(e) => handleTagClose(e, undefined, item)}>
                      <span className="secondary__color">{`${filterLabels?.[item]}: `}</span>
                      <span>
                        {`${dayjs(selectedFilters[item].start_date).format('D MMM YYYY')} to ${dayjs(
                          selectedFilters[item].end_date
                        ).format('D MMM YYYY')}`}
                      </span>
                    </Tag>
                  );
                }
                return selectedFilters?.[item]?.map((fv) => (
                  <Tag key={fv?.id} closable onClose={(e) => handleTagClose(e, fv, item)}>
                    {fv?.label || fv}
                  </Tag>
                ));
              })}
            </div>
            <Table
              className="por_table"
              columns={GetColumns(type)}
              dataSource={tableData}
              loading={loadingList}
              onRow={(record) => ({ onClick: () => handleRowClick(record) })}
              pagination={false}
              rowClassName="clickable-row"
              rowKey={(record) => record?.id}
              scroll={{
                x: 'max-content',
                y: tableHeight
              }}
            />
            <ConfigProvider
              theme={{
                token: {
                  borderRadius: 0,
                  colorPrimary: '#2CB695'
                }
              }}>
              <div className="custom_pagination">
                <CustomPagination onChange={handlePageChange} pagination={pagination} />
              </div>
            </ConfigProvider>
          </div>
        </div>
      ) : null}
    </AppLayout>
  );
};

export default Listing;

/**
 * {
  "po_number": [
    "string"
  ],
  "po_type": [
    "DOMESTIC_PO"
  ],
  "state": [
    "NEW"
  ],
  "brand": [
    "3fa85f64-5717-4562-b3fc-2c963f66afa6"
  ],
  "buyer": [
    "3fa85f64-5717-4562-b3fc-2c963f66afa6"
  ],
  "seller": [
    "3fa85f64-5717-4562-b3fc-2c963f66afa6"
  ],
  "currency": [
    "INR"
  ],
  "creation_date": {
    "start_date": "2023-10-17T10:13:55.029Z",
    "end_date": "2023-10-17T10:13:55.029Z"
  }
}
 */

/**
 * schema
 * types : array , date_range
 * {
 *  field: 'type'
 * }
 */
